import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CreditCard, FileText, AlertCircle, Plug } from "lucide-react";
import { financeApi, formatCents } from "@/services/finance";
import { PageShell, Pill, LoadingState } from "@/shared/ui/PageHeader";

export function FinanceDashboardPage() {
  const dash = useQuery({ queryKey: ["finance", "dashboard"], queryFn: financeApi.dashboard });
  if (dash.isLoading || !dash.data) return <LoadingState />;
  const d = dash.data;
  const qbStatusTone =
    d.qb_sync_status === "ok"
      ? "success"
      : d.qb_sync_status === "lagging"
        ? "warning"
        : d.qb_sync_status === "error"
          ? "danger"
          : "muted";

  return (
    <PageShell
      title="财务总览"
      subtitle="应收 / 应付 / 现金流 / QuickBooks 状态"
      actions={
        <Link
          to="/admin/finance/invoices/new"
          className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white"
        >
          <FileText className="h-3.5 w-3.5" /> 新建发票
        </Link>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Metric label="待开发票" value={String(d.pending_invoices_count)} />
        <Metric label="应收总额" value={formatCents(d.ar_total_cents)} />
        <Metric
          label="应收逾期"
          value={formatCents(d.ar_overdue_cents)}
          tone={d.ar_overdue_cents > 0 ? "danger" : "ok"}
        />
        <Metric label="本周应付" value={formatCents(d.ap_due_this_week_cents)} />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Plug className="h-4 w-4 text-slate-400" /> QuickBooks 集成状态
          </h3>
          <Link
            to="/admin/api-integration/qb"
            className="text-[12px] font-medium text-blue-600 hover:underline"
          >
            管理 →
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          <Pill tone={qbStatusTone}>
            {d.qb_sync_status === "disabled" ? "v1 未启用" : d.qb_sync_status}
          </Pill>
          <span className="text-slate-500">
            最近同步:{" "}
            {d.qb_last_sync_at ? new Date(d.qb_last_sync_at).toLocaleString("zh-CN") : "—"}
          </span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-500">v1.5 接 QB API · 业务壳已就位</span>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">30 天现金流</h3>
        </div>
        <div className="grid grid-cols-15 gap-px">
          {d.cashflow_30d.map((c, i) => {
            const net = c.in_cents - c.out_cents;
            const height = Math.max(8, Math.min(60, Math.abs(net) / 1500));
            return (
              <div key={i} className="flex flex-col items-center">
                <div
                  style={{ height: `${height}px` }}
                  className={`w-3 rounded-sm ${net >= 0 ? "bg-emerald-500" : "bg-rose-500"}`}
                  title={`${c.date}: 净 ${formatCents(net)}`}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
          <span>{d.cashflow_30d[0]?.date}</span>
          <span>{d.cashflow_30d[d.cashflow_30d.length - 1]?.date}</span>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <QuickLink to="/admin/finance/receivables" icon={CreditCard} label="应收账款" />
        <QuickLink to="/admin/finance/payables" icon={AlertCircle} label="应付账款" />
        <QuickLink to="/admin/finance/reports" icon={FileText} label="财务报表" />
      </div>
    </PageShell>
  );
}

function Metric({
  label,
  value,
  tone = "ok",
}: {
  label: string;
  value: string;
  tone?: "ok" | "danger";
}) {
  return (
    <div
      className={`rounded-xl border bg-white p-5 ${
        tone === "danger" ? "border-rose-200" : "border-slate-200"
      }`}
    >
      <div className="text-[12px] font-medium text-slate-500">{label}</div>
      <div
        className={`mt-2 text-[28px] font-bold ${
          tone === "danger" ? "text-rose-600" : "text-slate-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:bg-blue-50"
    >
      <Icon className="h-5 w-5 text-blue-600" />
      <span className="text-[13px] font-semibold text-slate-900">{label}</span>
    </Link>
  );
}
